import React from "react";
import { GridTable, GridHead, GridRow, GridCell } from "./ui/GridTable";
import { Num } from "./ui/Num";
import { BroadcastStatusBadge } from "./ui/Badge";
import { statusMeta, showsCounters } from "./broadcastStatus";
import {
  ROUTE_BY_ID,
  PUSH_SEGMENT_BY_ID,
  EMAIL_SEGMENT_BY_ID,
  CHANNEL_BY_ID,
  channelUsesPush,
  channelUsesEmail,
} from "./broadcastEnums";

/**
 * The Broadcasts list table: one row per broadcast, four counter columns.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS COMPONENT ENFORCES
 * ---------------------------------------------------------------------------
 * A scheduled (or draft) broadcast has no counters yet, so it renders `—`,
 * never `0`. Zero is a result; absence is not — `0 delivered` on a send that
 * has not started reads as a failure that has not happened.
 *
 * The gate is `showsCounters(statusMeta(row).key)`, NOT "the row's counters
 * happen to be null". That is belt-and-braces on purpose: a server that
 * helpfully serialises `sent_count: 0` — or a genuinely wrong `500` — for a
 * scheduled broadcast would otherwise paint four numbers onto a send that has
 * not addressed anybody. The status decides whether numbers exist at all; the
 * payload only decides what they are.
 *
 * Presentational only: no fetching, no services, no clock. Rows arrive already
 * DECODED (see `broadcastDecode.js`).
 */

/**
 * Broadcast | Status | Sent | Delivered | Opened | Clicked.
 *
 * The name track is `minmax(0,1fr)` so the title can actually truncate; the
 * four counter tracks are capped so a six-figure send cannot squeeze the name
 * out of existence.
 */
const COLUMNS = "minmax(0,1fr) 92px repeat(4,minmax(52px,78px))";

/** Written as one literal so Tailwind keeps it; assembled classes never ship. */
const META_TEXT = "text-xs text-bc-soft truncate";

/** The spec's separator for the meta line. Middle dot, spaced. */
const SEPARATOR = " · ";

/**
 * Resolve an enum id to its label, falling back to the raw id.
 *
 * An unrecognised value must stay VISIBLE. Returning "" for an id the server
 * added after we shipped would silently blank the meta line, which looks like
 * a broadcast with no audience rather than a console that is out of date.
 *
 * @param {Object<string, {label: string}>} byId - One of the `*_BY_ID` maps.
 * @param {string|null|undefined} id - The wire value.
 * @returns {string} The label, the raw id, or "" when there is no id at all.
 */
const labelFor = (byId, id) => {
  if (!id) return "";
  const known = byId[id];
  return known ? known.label : String(id);
};

/**
 * Format a date as "4 Aug".
 *
 * Returns "" for anything unparseable so the caller omits the part entirely.
 * `new Date("soon").toLocaleDateString()` is the string "Invalid Date", and
 * printing that in a list is worse than printing no date at all.
 *
 * @param {string|null|undefined} value - An ISO-ish date string.
 * @returns {string}
 */
const formatDay = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

/**
 * The secondary line under a broadcast's title: channels and their segments,
 * where a tap lands, and when it went (or goes) out.
 *
 * Empty parts are dropped rather than rendered as gaps, so a draft with no
 * route and no date shows a short line instead of " ·  · ".
 *
 * @param {Object} row - A decoded broadcast row.
 * @param {string} statusKey - `statusMeta(row).key`.
 * @returns {string}
 */
const metaLine = (row, statusKey) => {
  const parts = [];

  if (channelUsesPush(row.channels)) {
    parts.push(["Push", labelFor(PUSH_SEGMENT_BY_ID, row.pushSegment)].filter(Boolean).join(" "));
  }
  if (channelUsesEmail(row.channels)) {
    parts.push(["Email", labelFor(EMAIL_SEGMENT_BY_ID, row.emailSegment)].filter(Boolean).join(" "));
  }
  // A channel value the enum does not know is surfaced raw for the same reason
  // an unknown segment is: an invisible unknown is indistinguishable from none.
  if (row.channels && !CHANNEL_BY_ID[row.channels]) parts.push(String(row.channels));

  const routeLabel = labelFor(ROUTE_BY_ID, row.routeKey);
  if (routeLabel) parts.push(`opens ${routeLabel}`);

  const scheduledDay = statusKey === "scheduled" ? formatDay(row.scheduledAt) : "";
  if (scheduledDay) {
    parts.push(`scheduled ${scheduledDay}`);
  } else {
    const createdDay = formatDay(row.createdAt);
    if (createdDay) parts.push(createdDay);
  }

  return parts.filter(Boolean).join(SEPARATOR);
};

/**
 * One broadcast row.
 *
 * @param {Object} props
 * @param {Object} props.row - A decoded broadcast row.
 * @param {(id: string) => void} [props.onOpen] - Row activation handler.
 * @returns {React.ReactElement}
 */
const BroadcastRow = ({ row, onOpen }) => {
  const meta = statusMeta(row);
  const hasCounters = showsCounters(meta.key);

  // The single gate. Every counter goes through it, so no cell can opt out.
  const counter = (value) => (hasCounters ? value : null);

  const title = row.title || "Untitled broadcast";

  return (
    <GridRow
      label={`${title}, ${meta.label}`}
      onClick={onOpen ? () => onOpen(row.id) : undefined}
    >
      <GridCell>
        <div className="truncate font-medium text-bc-text">{title}</div>
        <div className={META_TEXT}>{metaLine(row, meta.key)}</div>
      </GridCell>
      <GridCell>
        <BroadcastStatusBadge row={row} />
      </GridCell>
      <GridCell numeric>
        <Num value={counter(row.sentCount)} />
      </GridCell>
      <GridCell numeric>
        <Num value={counter(row.deliveredCount)} />
      </GridCell>
      <GridCell numeric>
        <Num value={counter(row.openedCount)} />
      </GridCell>
      {/* Clicked is the one exact engagement number — opens are pixel-inflated,
          so only this column earns emphasis. */}
      <GridCell numeric>
        <Num value={counter(row.clickedCount)} emphasis />
      </GridCell>
    </GridRow>
  );
};

/**
 * The Broadcasts list table.
 *
 * Renders NOTHING when `rows` is empty — the page owns the empty state, because
 * only the page knows whether an empty list is a first run, a dead-end filter,
 * a permissions problem or a failed request. See `BroadcastEmpty`.
 *
 * @param {Object} props
 * @param {Array<Object>} props.rows - Decoded broadcast rows. Any counter may be null.
 * @param {(id: string) => void} [props.onOpen] - Called with the row id on click or Enter/Space. Rows are inert when omitted.
 * @param {string} [props.className] - Extra classes for the table element.
 * @returns {React.ReactElement|null}
 */
export const BroadcastRowGrid = ({ rows, onOpen, className = "" }) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <GridTable columns={COLUMNS} label="Broadcasts" className={className}>
      <GridHead>
        <GridCell head>Broadcast</GridCell>
        <GridCell head>Status</GridCell>
        <GridCell head numeric>
          Sent
        </GridCell>
        <GridCell head numeric>
          Delivered
        </GridCell>
        <GridCell head numeric>
          Opened
        </GridCell>
        <GridCell head numeric>
          Clicked
        </GridCell>
      </GridHead>
      {rows.map((row) => (
        <BroadcastRow key={row.id} row={row} onOpen={onOpen} />
      ))}
    </GridTable>
  );
};

export default BroadcastRowGrid;
